import React, { useState } from 'react';
import type { Organization, BusinessResiliencyPlanResult } from '@types';
import { ICONS } from './constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { HelpSidebar } from './HelpSidebar';
import { useGlobalAssets } from '../context/GlobalAssetContext';

interface BusinessResiliencyBuilderProps {
    isOpen: boolean;
    onClose: () => void;
    organization: Organization;
    onComplete: (result: BusinessResiliencyPlanResult) => void;
    inline?: boolean;
    onRequestHelp?: (preferChat?: boolean) => void;
    onNavigate?: (nav: { page: string }) => void;
}

export const BusinessResiliencyBuilder: React.FC<BusinessResiliencyBuilderProps> = ({ 
    isOpen, 
    onClose, 
    organization, 
    onComplete, 
    inline = false,
    onRequestHelp, 
    onNavigate 
}) => {
    const [step, setStep] = useState(1);
    
    // Form State
    const [criticalFunctions, setCriticalFunctions] = useState<string[]>([]);
    const [newFunction, setNewFunction] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    
    const { assets } = useGlobalAssets();
    
    // Engine State
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('');

    const PRESETS = [
        'IT Infrastructure / Data Centers',
        'Supply Chain / Logistics',
        'Human Resources / Payroll',
        'Financial Operations',
        'Customer Support / Call Centers',
        'Manufacturing / Production Lines'
    ];

    const toggleFunction = (func: string) => {
        setCriticalFunctions(prev => prev.includes(func) ? prev.filter(f => f !== func) : [...prev, func]);
    };

    const handleAddCustom = (e: React.FormEvent) => {
        e.preventDefault();
        if (newFunction.trim() && !criticalFunctions.includes(newFunction.trim())) {
            setCriticalFunctions([...criticalFunctions, newFunction.trim()]);
            setNewFunction('');
        }
    };

    const toggleAsset = (assetId: string) => {
        setSelectedAssetIds(prev => prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]);
    };

    const handleNext = () => {
        if (criticalFunctions.length === 0) return;
        setStep(2);
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setStep(3); // Loading step
        
        try {
            setLoadingText('Initializing Resiliency Architecture Core...');
            setProgress(15);
            await new Promise(r => setTimeout(r, 1200));

            setLoadingText(`Mapping dependencies for ${criticalFunctions.length} critical functions...`);
            setProgress(45);
            
            let result: BusinessResiliencyPlanResult;
            
            const functionsInst = getFunctions();
            const generateResiliencyPlan = httpsCallable(functionsInst, 'generateBusinessResiliencyPlan');
            const response = await generateResiliencyPlan({
                organizationId: organization.id,
                industry: 'Corporate', // Fallback, could inherit from Org
                criticalFunctions,
                assets: assets.filter(a => selectedAssetIds.includes(a.id)).map(a => a.name)
            });

            result = response.data as BusinessResiliencyPlanResult;
            setProgress(85);
            setLoadingText('Calculating optimal RTO/RPO targets...');
            await new Promise(r => setTimeout(r, 1000));
            
            setProgress(100);
            setTimeout(() => {
                onComplete(result);
            }, 800);

        } catch (error) {
            console.error("Business Resiliency Generation Error", error);
            setLoadingText('ERROR: AI Generation Failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in p-4">
            <div className={`absolute inset-0 bg-slate-900/90 backdrop-blur-xl ${!isGenerating ? 'cursor-pointer' : ''}`} onClick={!isGenerating ? onClose : undefined} />
            
            <div className="relative w-full max-w-6xl glass-panel flex flex-col md:flex-row transform transition-transform animate-scale-in text-left shadow-[0_0_100px_rgba(59,130,246,0.3)] border border-blue-500/50 rounded-2xl max-h-[90vh] overflow-hidden">
                
                {/* Main Content Area */}
                <div className="flex-1 p-8 overflow-y-auto custom-scrollbar relative">
                    {/* Header */}
                <div className="flex justify-between items-start mb-8 border-b border-slate-700/50 pb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400">
                                {ICONS.shield}
                            </div>
                            <h2 className="text-2xl font-black text-white tracking-widest uppercase">
                                Business Resiliency Framer
                            </h2>
                        </div>
                        <p className="text-sm text-slate-400 font-mono tracking-widest pl-12 uppercase">
                            Define critical business functions to generate continuity and recovery plans.
                        </p>
                    </div>
                    {!isGenerating && (
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition-colors">
                            {ICONS.menuClose}
                        </button>
                    )}
                </div>

                {step === 1 && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Select Critical Functions</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {PRESETS.map(preset => (
                                    <button
                                        key={preset}
                                        onClick={() => toggleFunction(preset)}
                                        className={`p-4 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${criticalFunctions.includes(preset) ? 'bg-emerald-600/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.2)] text-emerald-100' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500 text-slate-400'}`}
                                    >
                                        <span className="text-sm font-bold leading-tight">{preset}</span>
                                        {criticalFunctions.includes(preset) && (
                                            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Add Custom Function</label>
                            <form onSubmit={handleAddCustom} className="flex gap-2">
                                <input 
                                    type="text"
                                    value={newFunction}
                                    onChange={(e) => setNewFunction(e.target.value)}
                                    placeholder="e.g., Executive Protection Detail..."
                                    className="flex-grow bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors placeholder:text-slate-600"
                                />
                                <button 
                                    type="submit"
                                    disabled={!newFunction.trim()}
                                    className="px-6 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:hover:bg-slate-700 text-white font-bold rounded transition-colors uppercase tracking-widest text-xs"
                                >
                                    Add
                                </button>
                            </form>
                            
                            {/* Render Custom Chips */}
                            <div className="flex flex-wrap gap-2 pt-2">
                                {criticalFunctions.filter(f => !PRESETS.includes(f)).map(customFunc => (
                                    <span key={customFunc} className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/50 rounded-full text-emerald-300 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                                        {customFunc}
                                        <button onClick={() => toggleFunction(customFunc)} className="hover:text-emerald-100">&times;</button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-slate-800">
                            <button 
                                onClick={handleNext}
                                disabled={criticalFunctions.length === 0}
                                className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:hover:bg-emerald-600 text-white font-bold rounded shadow-lg shadow-emerald-500/20 transition-all uppercase tracking-widest flex items-center gap-2"
                            >
                                Next: Map Assets &rarr;
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="space-y-4">
                            <label className="block text-sm font-bold text-slate-300 uppercase tracking-widest">Map to Global Assets</label>
                            <p className="text-sm text-slate-400">Select the physical or digital assets where these critical functions operate.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {assets.map(asset => (
                                    <button
                                        key={asset.id}
                                        onClick={() => toggleAsset(asset.id)}
                                        className={`p-4 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${selectedAssetIds.includes(asset.id) ? 'bg-emerald-600/20 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.2)] text-emerald-100' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500 text-slate-400'}`}
                                    >
                                        <div>
                                            <span className="text-sm font-bold leading-tight block">{asset.name}</span>
                                            <span className="text-xs text-slate-500 uppercase">{asset.type}</span>
                                        </div>
                                        {selectedAssetIds.includes(asset.id) && (
                                            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        )}
                                    </button>
                                ))}
                                {assets.length === 0 && (
                                    <div className="col-span-2 p-4 text-center text-slate-500 border border-slate-700 rounded-lg bg-slate-800/30">
                                        No assets configured in Global Context.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-between pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => setStep(1)}
                                className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded transition-all uppercase tracking-widest"
                            >
                                &larr; Back
                            </button>
                            <button 
                                onClick={handleGenerate}
                                className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded shadow-lg shadow-emerald-500/20 transition-all uppercase tracking-widest flex items-center gap-2"
                            >
                                {ICONS.shield}
                                Architect Resiliency Plan
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="py-20 flex flex-col items-center justify-center animate-fade-in text-center">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-emerald-500/20 blur-[50px] rounded-full animate-pulse" />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-24 h-24 text-emerald-500 relative z-10 animate-[spin_4s_linear_infinite]">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.664 0l3.181-3.183m-4.991-2.691L7.985 5.989m11.664 0l-3.181 3.183a8.25 8.25 0 01-11.664 0l-3.181-3.183" />
                            </svg>
                        </div>
                        
                        <p className="text-emerald-400 font-mono text-sm tracking-widest mb-10 h-6">
                            {loadingText}
                        </p>

                        <div className="w-full max-w-lg bg-slate-800 rounded-full h-2 mb-4 overflow-hidden border border-slate-700">
                            <div 
                                className="bg-gradient-to-r from-emerald-600 to-teal-400 h-2 rounded-full transition-all duration-500 ease-out relative" 
                                style={{ width: `${progress}%` }}
                            >
                                <div className="absolute top-0 right-0 bottom-0 w-20 bg-white/30 blur-[4px]" />
                            </div>
                        </div>
                    </div>
                )}
                </div>

                {/* Sidebar */}
                <HelpSidebar 
                    title="Resiliency Architect Support"
                    description="Configure continuity systems and establish recovery time objectives."
                    instructions={[
                        "Select standard critical functions from the presets.",
                        "Add unique custom functions critical to your operations.",
                        "Map functions to specific physical or digital assets.",
                        "Click 'Architect Resiliency Plan' to generate RTOs and phases."
                    ]}
                    onRequestHelp={onRequestHelp}
                    onContactConsultant={() => onNavigate && onNavigate({ page: 'contact' })}
                    theme="dark"
                />
            </div>
        </div>
    );
};
